import { useState } from "react";
import {
  required,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import { AnnualFeeModes, CitizenshipIds, SELECT_ALL } from "utils/constants";
import { AnnualFeeModeChoices } from "utils/choices";
import { administrationRequests } from "api/administrationApi";
import { CALCULATED_FEE_FIELDS, deriveFeeMode, euroFormat, euroParse } from "../utils";

const PaidEducationFeeFields = ({ disabled }) => {
  const { index } = useSimpleFormIteratorItem();
  const { getValues, setValue } = useFormContext();
  const watchPrefix = `paidEducationFees.${index}`;

  const citizenship = useWatch({ name: `${watchPrefix}.citizenship` });
  const feeMode = useWatch({ name: `${watchPrefix}.feeMode` });

  const [defaultFeeMode] = useState(() => deriveFeeMode(getValues(watchPrefix)));

  const isBulgarian = citizenship === CitizenshipIds.Bulgarians;
  const isFixedAmount = Number(feeMode ?? defaultFeeMode) === AnnualFeeModes.Fixed;

  const handleFeeModeChange = event => {
    const nextMode = Number(event?.target?.value ?? event);
    if (nextMode === AnnualFeeModes.Fixed) {
      CALCULATED_FEE_FIELDS.forEach(field => setValue(`${watchPrefix}.${field}`, null));
    } else {
      setValue(`${watchPrefix}.fixedAmountEuro`, null);
    }
  };

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="citizenship"
          reference={administrationRequests.getCitizenshipSelect()}
          label="Гражданство"
        >
          <SelectInput
            optionText="name"
            label="Гражданство"
            disabled={disabled}
            validate={required()}
            fullWidth
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="studyTypeId"
          reference={administrationRequests.getStudyTypesSelect()}
          label="Вид на обучение"
        >
          <SelectInput
            optionText="name"
            label="Вид на обучение *"
            disabled={disabled}
            defaultValue={SELECT_ALL}
            emptyValue={SELECT_ALL}
            emptyText={"Всички"}
            fullWidth
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="admissionReasonId"
          reference={administrationRequests.getAdmissionReasonsSelect()}
          label="Основание за прием"
        >
          <SelectInput
            optionText="name"
            optionValue="id"
            label="Основание за прием"
            helperText="Оставете „Всички“ за общата такса"
            disabled={disabled}
            defaultValue={SELECT_ALL}
            emptyValue={SELECT_ALL}
            emptyText={"Всички"}
            fullWidth
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="taxType"
          reference={administrationRequests.getPaidEducationAnnualTaxTypesSelect()}
        >
          <SelectInput
            optionText="nameBg"
            label="Вид такса"
            disabled={disabled}
            validate={required()}
            fullWidth
          />
        </ReferenceInput>
      </Grid>

      {citizenship && !isBulgarian ? (
        <>
          <Grid
            item
            xs={12}
            md={3}
          >
            <ReferenceInput
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
            >
              <SelectInput
                optionText="name"
                optionValue="id"
                label="Език на обучение"
                defaultValue={SELECT_ALL}
                emptyValue={SELECT_ALL}
                emptyText={"Всички"}
                disabled={disabled}
                fullWidth
              />
            </ReferenceInput>
          </Grid>

          <Grid
            item
            xs={12}
            md={3}
          >
            <NumberInput
              source="fixedAmountEuro"
              label="Цена в евро"
              disabled={disabled}
              format={euroFormat}
              parse={euroParse}
              validate={required()}
              onWheel={e => e.target.blur()}
            />
          </Grid>
        </>
      ) : null}

      {citizenship && isBulgarian ? (
        <>
          <Grid
            item
            xs={12}
          >
            <RadioButtonGroupInput
              source="feeMode"
              label="Начин на определяне"
              choices={AnnualFeeModeChoices}
              defaultValue={defaultFeeMode}
              disabled={disabled}
              onChange={handleFeeModeChange}
            />
          </Grid>

          {isFixedAmount ? (
            <Grid
              item
              xs={12}
              md={3}
            >
              <NumberInput
                source="fixedAmountEuro"
                label="Твърда цена в евро"
                disabled={disabled}
                format={euroFormat}
                parse={euroParse}
                validate={required()}
                onWheel={e => e.target.blur()}
              />
            </Grid>
          ) : (
            <>
              <Grid
                item
                xs={12}
                md={2.4}
              >
                <NumberInput
                  source="baseNormEuro"
                  label="БН в евро"
                  disabled={disabled}
                  format={euroFormat}
                  parse={euroParse}
                  validate={required()}
                  onWheel={e => e.target.blur()}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2.4}
              >
                <NumberInput
                  source="stateFeeEuro"
                  label="Държавна такса (евро)"
                  disabled={disabled}
                  format={euroFormat}
                  parse={euroParse}
                  validate={required()}
                  onWheel={e => e.target.blur()}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2.4}
              >
                <NumberInput
                  source="professionalFieldCoefficient"
                  label="КПН"
                  disabled={disabled}
                  validate={required()}
                  onWheel={e => e.target.blur()}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2.4}
              >
                <NumberInput
                  source="compositeAssessmentCoefficient"
                  label="ККО"
                  disabled={disabled}
                  validate={required()}
                  onWheel={e => e.target.blur()}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2.4}
              >
                <NumberInput
                  source="discountAmountEuro"
                  label="Отстъпка в евро"
                  disabled={disabled}
                  format={euroFormat}
                  parse={euroParse}
                  validate={required()}
                  onWheel={e => e.target.blur()}
                />
              </Grid>
            </>
          )}
        </>
      ) : null}
    </Grid>
  );
};

export default PaidEducationFeeFields;
